import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronUp, ChevronDown, Calendar, User, Gift, Trophy, Sparkles, Loader2 } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { Link } from 'react-router-dom';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Icon mapping
const ICONS = {
  Sparkles,
  User,
  Gift,
  Trophy,
  Calendar
};

// Fallback CTAs if API fails
const fallbackCTAs = [
  {
    id: 'prova',
    icon: 'Sparkles',
    title: { ca: 'Prova el Golf', es: 'Prueba el Golf', fr: 'Essayez le Golf', en: 'Try Golf' },
    link: '/iniciacio',
    external: false,
    image: 'https://images.unsplash.com/photo-1662718552420-f612c0408be8?crop=entropy&cs=srgb&fm=jpg&w=400&q=80'
  },
  {
    id: 'abonat',
    icon: 'User',
    title: { ca: 'Fes-te Abonat', es: 'Hazte Abonado', fr: 'Devenez Membre', en: 'Become a Member' },
    link: '/membres',
    external: false,
    image: 'https://images.unsplash.com/photo-1636315662384-8406112deaf1?crop=entropy&cs=srgb&fm=jpg&w=400&q=80'
  },
  {
    id: 'ofertes',
    icon: 'Gift',
    title: { ca: 'Ofertes', es: 'Ofertas', fr: 'Offres', en: 'Offers' },
    link: '/jugar',
    external: false,
    image: 'https://images.pexels.com/photos/137523/pexels-photo-137523.jpeg?auto=compress&w=400'
  },
  {
    id: 'torneig',
    icon: 'Trophy',
    title: { ca: 'Torneig', es: 'Torneo', fr: 'Tournoi', en: 'Tournament' },
    link: '/competicio',
    external: false,
    image: 'https://images.unsplash.com/photo-1587174486073-ae5e5cff23aa?q=80&w=400'
  },
  {
    id: 'reserva',
    icon: 'Calendar',
    title: { ca: 'Reserva', es: 'Reserva', fr: 'Réserver', en: 'Book Now' },
    link: 'https://open.teeone.golf/es/aravell',
    external: true,
    image: 'https://images.unsplash.com/photo-1535131749006-b7f58c99034b?q=80&w=400'
  }
];

export const FloatingCTA = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [currentCTA, setCurrentCTA] = useState(0);
  const [ctaItems, setCTAItems] = useState(fallbackCTAs);
  const [loading, setLoading] = useState(true);
  const { language } = useLanguage();

  useEffect(() => {
    const fetchCTAs = async () => {
      try {
        const response = await fetch(`${API_URL}/api/ctas`);
        if (response.ok) {
          const data = await response.json();
          if (data.ctas && data.ctas.length > 0) {
            setCTAItems(data.ctas);
          }
        }
      } catch (error) {
        console.error('Failed to fetch CTAs:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCTAs();
  }, []);

  const nextCTA = () => {
    setCurrentCTA((prev) => (prev + 1) % ctaItems.length);
  };

  const prevCTA = () => {
    setCurrentCTA((prev) => (prev - 1 + ctaItems.length) % ctaItems.length);
  };

  if (loading || ctaItems.length === 0) {
    return null;
  }

  const currentItem = ctaItems[currentCTA];
  const IconComponent = ICONS[currentItem.icon] || Sparkles;
  const title = currentItem.title?.[language] || currentItem.title?.ca || 'CTA';

  return (
    <div className="floating-cta" data-testid="floating-cta">
      <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
        {/* Collapsed state - just the toggle button */}
        <CollapsibleTrigger asChild>
          <motion.button
            className={`w-full flex items-center justify-between px-5 py-4 bg-[#1A4D2E] text-white font-medium text-sm tracking-wide uppercase transition-all duration-300 ${
              isExpanded ? 'rounded-t-none' : ''
            }`}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            data-testid="floating-cta-toggle"
          >
            <span className="flex items-center gap-2">
              <IconComponent className="w-4 h-4" />
              {title}
            </span>
            {isExpanded ? (
              <ChevronDown className="w-5 h-5" />
            ) : (
              <ChevronUp className="w-5 h-5" />
            )}
          </motion.button>
        </CollapsibleTrigger>

        {/* Expanded content */}
        <CollapsibleContent>
          <motion.div 
            className="bg-white shadow-[0_12px_48px_rgba(26,77,46,0.15)] overflow-hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {/* CTA Carousel */}
            <div className="relative">
              {/* Current CTA Card */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentItem.id}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                  className="p-4"
                >
                  {/* Image - 600x400 ratio (3:2) */}
                  <div className="relative aspect-[3/2] overflow-hidden mb-4">
                    <img 
                      src={currentItem.image} 
                      alt={title}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  {/* CTA Button */}
                  {currentItem.external ? (
                    <a
                      href={currentItem.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block w-full bg-[#D4AF37] text-[#1A1A1A] text-center py-3 text-xs font-semibold tracking-[0.15em] uppercase hover:bg-[#c4a030] transition-colors duration-200"
                      data-testid={`cta-link-${currentItem.id}`}
                    >
                      {title}
                    </a>
                  ) : (
                    <Link
                      to={currentItem.link}
                      className="block w-full bg-[#1A4D2E] text-white text-center py-3 text-xs font-semibold tracking-[0.15em] uppercase hover:bg-[#153d24] transition-colors duration-200"
                      data-testid={`cta-link-${currentItem.id}`}
                    >
                      {title}
                    </Link>
                  )}
                </motion.div>
              </AnimatePresence>

              {/* Navigation dots */}
              <div className="flex items-center justify-center gap-2 pb-4">
                <button 
                  onClick={prevCTA}
                  className="p-1 text-[#666666] hover:text-[#1A4D2E] transition-colors duration-200"
                  aria-label="Previous CTA"
                  data-testid="cta-prev"
                >
                  <ChevronDown className="w-4 h-4 rotate-90" />
                </button>
                
                {ctaItems.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentCTA(index)}
                    className={`w-2 h-2 rounded-full transition-all duration-300 ${
                      currentCTA === index 
                        ? 'w-6 bg-[#1A4D2E]' 
                        : 'bg-[#E5E5E5] hover:bg-[#D4AF37]'
                    }`}
                    aria-label={`Go to CTA ${index + 1}`}
                    data-testid={`cta-dot-${index}`}
                  />
                ))}
                
                <button 
                  onClick={nextCTA}
                  className="p-1 text-[#666666] hover:text-[#1A4D2E] transition-colors duration-200"
                  aria-label="Next CTA"
                  data-testid="cta-next"
                >
                  <ChevronDown className="w-4 h-4 -rotate-90" />
                </button>
              </div>
            </div>
          </motion.div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};

export default FloatingCTA;
